import {
  MyFundsDataSourceType,
  MyFundsDataSourceTypeWithoutKey,
  Metric,
  ColumnType,
  TableCellType,
  CellType,
} from "../../models/tableModels";
import {
  Filter as Option,
  ColumnFilter as ColumnOption,
} from "../../types/PageFilterTypes";
import { SelectedIndexes } from "./MyFundsTable";
import { compassBordeaux, compassLightGreen, darkGreen } from "../../styles";
import {
  DEFAULT_COMPARABLE_COLUMNS,
  FUND_KEY_PREFIX,
  MY_FUNDS_COLUMN_TYPES,
  PORTFOLIO_INDEX_ID_COLUMN,
} from "./constants";
import { MESSAGES } from "../../messages";
import { formatReadableDate } from "../../utils/formattingUtils";
import { DeletePortfolioProps } from "./DeletePortfolio";
import {
  checkAlignment,
  getPathwayFromAlignment,
} from "../../components/visuals/PathwayProgressBar";

/**
 * Generates a list of unique column options for display based on provided metrics data.
 * It uses a set to ensure uniqueness and constructs an array of option objects,
 * each with a metric ID as key and a display name.
 *
 * @param data Array of metrics, each containing details like metric ID and display name.
 * @returns An array of unique display options for metrics.
 */
export const getDynamicColumns = (data: Metric[]): Option[] => {
  const uniqueMetrics = new Set();

  const resultArray = data.reduce((acc: Option[], item: Metric) => {
    if (!uniqueMetrics.has(item.metric_id)) {
      uniqueMetrics.add(item.metric_id);
      acc.push({
        key: item.metric_id,
        displayValue: item.Compass_Metric_Display_Name,
      });
    }
    return acc;
  }, []);

  return resultArray;
};

/**
 * Returns the column type based on the provided column key and value.
 * @param column - The column key.
 * @param value - The value to determine the column type.
 * @param loading - Optional. Indicates if the table cell is in a loading state. Default is false.
 * @returns The column type object.
 */
export const getColumnTypeFromKeyOrValue = (
  column: string,
  value: string | number | boolean | DeletePortfolioProps,
  loading = false
): ColumnType => {
  if (loading) return { columnKey: column, type: TableCellType.LOADING };
  if (value === MESSAGES.NOT_AVAILABLE)
    return { columnKey: column, type: TableCellType.TEXT };
  for (const columnType of MY_FUNDS_COLUMN_TYPES) {
    if (columnType.columnKey === column) {
      return columnType;
    }
  }

  // Fallback based on the type of value
  if (typeof value === "number") {
    return { columnKey: column, type: TableCellType.NUMBER };
  }
  return { columnKey: column, type: TableCellType.TEXT };
};

/**
 * Constructs and formats a structured data source suited for antd table datasource for funds from provided metrics.
 * It organizes data into indexed and non-indexed funds, assigns unique keys,
 * and interleaves indexes with portfolio data for structured presentation.
 *
 * @param data Array containing various metrics for different funds.
 * @returns A two-dimensional array with separate lists for interleaved funds and indexes.
 */
export const buildMyFundsDataSource = (
  data: Metric[]
): MyFundsDataSourceType[][] => {
  const allFunds: MyFundsDataSourceTypeWithoutKey[] = [];

  data.forEach((item) => {
    let existingItem = allFunds.find(
      (res) => res.portfolio_index_id === item.portfolio_index_id
    );

    if (!existingItem) {
      existingItem = {
        portfolio_index_id: item.portfolio_index_id,
        currency: item.currency,
        fund_aum: item.is_index ? 0 : item.fund_aum,
        added_on: formatReadableDate(item.added_on),
        is_index: item.is_index,
        pathway: getPathwayFromAlignment(checkAlignment(item)),
        delete: {
          isIndex: item.is_index,
          portfolioId: item.portfolio_index_id,
        },
      };
      allFunds.push(existingItem);
    }

    existingItem[item.metric_id] =
      item.value_numeric ||
      (item.value_text !== "0" ? item.value_text : "") ||
      "";
  });
  const indexesWithoutKey: MyFundsDataSourceTypeWithoutKey[] = allFunds.filter(
    (item) => item.is_index
  );
  const placeholderIndex: MyFundsDataSourceTypeWithoutKey =
    indexesWithoutKey[0];

  const portfolios: MyFundsDataSourceTypeWithoutKey[] = allFunds.filter(
    (item) => !item.is_index
  );

  const interleavedFunds: MyFundsDataSourceTypeWithoutKey[] = [];

  // Interleave placeholderIndex with portfolio items
  for (const portfolio of portfolios) {
    interleavedFunds.push(portfolio);
    interleavedFunds.push(placeholderIndex);
  }

  const result: MyFundsDataSourceType[] = [];
  interleavedFunds.forEach((item, index) => {
    const newItem = { ...item, key: `fund_${index}` };
    result.push(newItem);
  });

  const indexes: MyFundsDataSourceType[] = [];
  indexesWithoutKey.forEach((item, index) => {
    const newItem = { ...item, key: `index_${index}` };
    indexes.push(newItem);
  });

  return [result, indexes];
};

/**
 * Combines base columns with selected columns to create a comprehensive list of columns.
 * This combined list is used to configure the display and interactions within the UI.
 *
 * @param baseColumns Predefined or default columns.
 * @param selectedColumns Columns selected by the user or through specific UI interactions.
 * @returns An array containing all columns merged from base and selected ones.
 */
export const getAllMyFundsColumns = (
  baseColumns: ColumnOption[],
  selectedColumns: ColumnOption[]
): ColumnOption[] => {
  return [...baseColumns, ...selectedColumns];
};

/**
 * Retrieves a specific value from the funds data using portfolio index ID and a key.
 * This function searches for a fund record matching the ID and returns the value associated with the key.
 *
 * @param data Complete data set of fund records.
 * @param portfolioIndexId ID used to find the specific fund record.
 * @param key Metric key for which the value is needed.
 * @returns The value for the specified key within the matched fund record or "N/A" if no record is found.
 */
export const getValueByPortfolioIndexIdAndKey = (
  data: MyFundsDataSourceType[],
  portfolioIndexId: string,
  key: string
): CellType => {
  const filteredData = data.find(
    (item) => item.portfolio_index_id === portfolioIndexId
  );

  if (filteredData && key in filteredData && filteredData[key] !== 0) {
    return filteredData[key];
  }
  return MESSAGES.NOT_AVAILABLE;
};

export const addIndexSelectorName = (
  indexes: Option[],
  selectorName: string
): Option[] => {
  return indexes.map((index) => ({
    ...index,
    selectorName: selectorName,
  }));
};

export const buildIndexOptions = (data: MyFundsDataSourceType[]): Option[] => {
  return data.map((item) => ({
    displayValue: item.portfolio_index_id,
    key: item.portfolio_index_id,
  }));
};

export const isRecordIndex = (
  record: MyFundsDataSourceType,
  column: Option
): boolean => record.is_index && column.key === PORTFOLIO_INDEX_ID_COLUMN;

export const getComparatorIndexFundKey = (fundKey: string): string => {
  const startIndex = fundKey.indexOf(FUND_KEY_PREFIX);

  if (startIndex === -1) {
    return fundKey;
  }

  const numberPart = fundKey.substring(startIndex + FUND_KEY_PREFIX.length);

  const decrementedNumber = parseInt(numberPart, 10) + 1;

  if (isNaN(decrementedNumber) || decrementedNumber < 0) {
    return fundKey;
  }

  return `${FUND_KEY_PREFIX}${decrementedNumber}`;
};

export const getIndexBySelectorName = (
  selectorName: string,
  selectedIndexes: SelectedIndexes
): string | null => {
  const selectedIndex: Option = selectedIndexes[selectorName];
  return (selectedIndex?.key as string) || null;
};

/**
 * Determines the font color for a table cell based on the comparison of values from a portfolio
 * and its corresponding index. It uses the column type to decide if a comparison should be made
 * and assigns colors based on whether the portfolio value is lesser, greater, or equal to the index value.
 *
 * @param columnType The type and properties of the column being evaluated.
 * @param valuePortfolio The value associated with the portfolio.
 * @param valueIndex The comparable index value.
 * @returns A string indicating the font color based on the comparison results.
 */
export const getFontColorFromValuesAndColumnType = (
  columnType: ColumnType,
  valuePortfolio: CellType,
  valueIndex: CellType
): string => {
  if (
    DEFAULT_COMPARABLE_COLUMNS.includes(columnType.type) &&
    columnType.isComparative !== false &&
    valuePortfolio !== MESSAGES.NOT_AVAILABLE &&
    valueIndex !== MESSAGES.NOT_AVAILABLE
  ) {
    if (valuePortfolio <= valueIndex) {
      return compassLightGreen;
    }
    return compassBordeaux;
  }
  return darkGreen;
};
